import { observer } from 'mobx-react-lite'
import { Linking, Platform, StyleSheet, View } from 'react-native'

import { Player } from '@ppwr/poker'

import { useStore } from '../../../../../models'
import { events } from '../../../../../models/analytics/analytics-events'
import { PlayerAVStatusType } from '../../../../../models/communication/agora.store'
import { useAVOptions } from '../../../../../screens/new-design/hooks/useAVOptions'
import { color } from '../../../../../theme'
import CameraIconOff from '../../../assets/Camera--off.svg'
import MicIconOff from '../../../assets/Mic--off.svg'
import { AVControl } from '../../../AVControl'
import { DS } from '../../../../../design-system'
import { PlayerCellVideo } from '../../../PlayerCell/PlayerCellVideo'
import {
  hasCameraPermission,
  hasMicrophonePermission,
  requestCameraPermission,
  requestMicrophonePermission
} from '../../../../../screens/new-design/hooks/useJoinCall'
import { useEffect, useState } from 'react'
// import { ClientRoleType } from 'react-native-agora'
import { ClientRoleType } from '@/modules/agora'
import { BottomSheetView } from '@gorhom/bottom-sheet'
import { useAVPermissions } from '../../../ModalContent/Content/AVPermissions/AVPermissionsModal'

const goToSettings = () => {
  if (Platform.OS === 'ios') {
    Linking.openURL('app-settings:')
  } else {
    Linking.openSettings()
  }
}

export interface CallOptionsProps {
  onLeaveCall?: () => void
  onRemovePlayer?: () => void
  onClose: () => void
  isTeacher?: boolean
  forPlayer?: Player
  playerAVStatus?: PlayerAVStatusType
  showPlayerControls?: boolean
  isTeacherOpeningOtherPlayer?: boolean
}

export const CallOptions = observer(
  ({
    onClose,
    onRemovePlayer,
    forPlayer,
    playerAVStatus,
    showPlayerControls,
    isTeacherOpeningOtherPlayer
  }: CallOptionsProps) => {
    const store = useStore()
    const { isAudiOff, isVideoOff, muteCall, unMuteCall, showVideo, hideVideo } = useAVOptions()
    const [isVideoOffStatus, setIsVideoOffStatus] = useState(isVideoOff)
    const [isAudioOffStatus, setIsAudioOffStatus] = useState(isAudiOff)
    const { setPermissionConfig } = useAVPermissions()

    useEffect(() => {
      setIsVideoOffStatus(isVideoOff)
    }, [isVideoOff])

    useEffect(() => {
      setIsAudioOffStatus(isAudiOff)
    }, [isAudiOff])

    const isOtherPlayerOnAVCall = isTeacherOpeningOtherPlayer && playerAVStatus?.isPlayerOnAudioCall

    return (
      <BottomSheetView style={Platform.OS !== 'web' ? { flex: 1 } : {}}>
        <View style={styles.bottomSheetContainer}>
          {forPlayer ? <DS.Text variant={'Heading 2/Regular'}>{forPlayer.name}</DS.Text> : null}
          <View style={styles.container}>
            {showPlayerControls && !isTeacherOpeningOtherPlayer ? (
              <View style={styles.avControlsContainer}>
                <AVControl
                  name="Camera"
                  statusText={isVideoOff ? 'Off' : 'On'}
                  icon={
                    isVideoOff ? (
                      <CameraIconOff />
                    ) : (
                      <PlayerCellVideo videoStyle={{ borderRadius: 100, width: 56, height: 56 }} />
                    )
                  }
                  isSwitchEnabled={!isVideoOffStatus}
                  isTeacherOpeningOtherPlayer={isTeacherOpeningOtherPlayer}
                  customCircleStyle={{
                    backgroundColor: isVideoOff ? color.palette.lightGrey : color.palette.white,
                    borderWidth: isVideoOff ? 0 : 1
                  }}
                  onPress={async () => {
                    setIsVideoOffStatus(!isVideoOffStatus)
                    if (isOtherPlayerOnAVCall) {
                      //TODO: teacher can mute/unmute other player, no-op for now
                      return
                    }
                    if (isVideoOffStatus) {
                      if (Platform.OS === 'web') {
                        let hasMicPermissionOn = await hasMicrophonePermission()
                        let hasCamPermissionOn = await hasCameraPermission()

                        let allowPrompted = false

                        if (!(hasMicPermissionOn && hasCamPermissionOn)) {
                          setPermissionConfig({
                            isAVPermissionsOpen: true,
                            isAllowPrompted: true
                          })
                          allowPrompted = true
                        }

                        hasMicPermissionOn = await requestMicrophonePermission()

                        if (!hasMicPermissionOn) {
                          setIsVideoOffStatus(true)
                          setPermissionConfig({
                            isAVPermissionsOpen: true,
                            isAllowPrompted: false,
                            blockedPermission: 'microphone'
                          })
                          return
                        }

                        hasCamPermissionOn = await requestCameraPermission()
                        if (!hasCamPermissionOn) {
                          setIsVideoOffStatus(true)
                          setPermissionConfig({
                            isAVPermissionsOpen: true,
                            isAllowPrompted: false,
                            blockedPermission: 'camera'
                          })
                          return
                        }

                        if (allowPrompted) {
                          setPermissionConfig({
                            isAVPermissionsOpen: false
                          })
                        }
                      } else {
                        let hasCamPermission = await hasCameraPermission()
                        if (!hasCamPermission) {
                          hasCamPermission = await requestCameraPermission()
                        }
                        let hasMicPermission = await hasMicrophonePermission()
                        if (!hasMicPermission) {
                          hasMicPermission = await requestMicrophonePermission()
                        }
                        if (!(hasCamPermission && hasMicPermission)) {
                          setTimeout(() => {
                            goToSettings()
                            setIsVideoOffStatus(true)
                          }, 50)
                          return
                        }
                      }

                      // This also asks for audio permission, but we are doing this above, but this is required to start streaming both audio and video.
                      store.communication.rtcEngine?.setClientRole(ClientRoleType.ClientRoleBroadcaster)
                      store.communication.rtcEngine?.enableVideo()

                      if (isAudioOffStatus) {
                        store.communication.muteVideoCall()
                      }

                      showVideo()
                      store.analytics.setEvent(events.homescreen.video_toggled_on)
                    } else {
                      hideVideo()
                      store.analytics.setEvent(events.homescreen.video_toggled_off)
                    }
                  }}
                />
                <AVControl
                  name="Microphone"
                  statusText={isAudiOff ? 'Off' : 'On'}
                  icon={isAudiOff ? <MicIconOff /> : <DS.Icon name="Audio" size={24} color="Global/Black" />}
                  isSwitchEnabled={!isAudioOffStatus}
                  isTeacherOpeningOtherPlayer={isTeacherOpeningOtherPlayer}
                  customCircleStyle={{
                    backgroundColor: isAudiOff ? color.palette.lightGrey : color.palette.white,
                    borderWidth: isAudiOff ? 0 : 1
                  }}
                  onPress={async () => {
                    setIsAudioOffStatus(!isAudioOffStatus)
                    // onClose()
                    if (isOtherPlayerOnAVCall) {
                      //TODO: teacher can mute/unmute other player, no-op for now
                      return
                    }
                    if (isAudioOffStatus) {
                      if (Platform.OS === 'web') {
                        let hasMicPermissionOn = await hasMicrophonePermission()

                        let allowPrompted = false

                        if (!hasMicPermissionOn) {
                          setPermissionConfig({
                            isAVPermissionsOpen: true,
                            isAllowPrompted: true
                          })
                          allowPrompted = true
                        }

                        hasMicPermissionOn = await requestMicrophonePermission()

                        if (!hasMicPermissionOn) {
                          setPermissionConfig({
                            isAVPermissionsOpen: true,
                            isAllowPrompted: false,
                            blockedPermission: 'microphone'
                          })
                          setIsAudioOffStatus(true)
                          return
                        }

                        if (allowPrompted) {
                          setPermissionConfig({
                            isAVPermissionsOpen: false
                          })
                        }
                      } else {
                        let hasMicPermission = await hasMicrophonePermission()
                        if (!hasMicPermission) {
                          hasMicPermission = await requestMicrophonePermission()
                        }
                        if (!hasMicPermission) {
                          setTimeout(() => {
                            goToSettings()
                            setIsAudioOffStatus(true)
                          }, 50)
                          return
                        }
                      }

                      if (Platform.OS !== 'web') {
                        // This also asks for audio permission, but we are doing this above, but this is required to start streaming audio.
                        store.communication.rtcEngine?.setClientRole(ClientRoleType.ClientRoleBroadcaster)
                        store.communication.rtcEngine?.enableAudio()
                      }

                      unMuteCall()
                      store.analytics.setEvent(events.homescreen.audio_toggled_on)
                    } else {
                      muteCall()
                      store.analytics.setEvent(events.homescreen.audio_toggled_off)
                    }
                  }}
                />
              </View>
            ) : null}

            <DS.Button variant="Large/Secondary" type={'On Dark'} onPress={onClose}>
              Done
            </DS.Button>
          </View>
        </View>
      </BottomSheetView>
    )
  }
)

const styles = StyleSheet.create({
  bottomSheetContainer: {
    flex: 1,
    paddingHorizontal: DS.spacing['16'],
    paddingTop: DS.spacing['8'],
    alignItems: 'center',
    gap: DS.spacing['16']
  },
  container: {
    gap: 20,
    width: '100%',
    justifyContent: 'center'
  },
  avControlsContainer: {
    justifyContent: 'center',
    gap: DS.spacing['16']
  }
})
